import React, { lazy, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useCart } from "../CartContext";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../helper/formatNumber";
import { useRestaurantDetails } from "../context/RestaurantDetailsContext";
import { formatCurrency } from "../helper/formatCurrency";
import { getAppConfig } from "../utilis/appConfig";
import { enqueueSnackbar } from "notistack";
import {
  isMultiVariant,
  cheapestVariant,
  variantPrice,
  variantLineId,
  useVariantUnitLabel,
} from "../helper/variants";


//const currency = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
const currency = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
});

function MenuItem(props) {
  const { updateTotalPrice, totalPrice, itemQuantities, updateQuantity } =
    useCart();
  const menuItem = props.MenuItem;
  const cart = props.cart;
  const { restaurantDetails } = useRestaurantDetails();
  const locale = restaurantDetails?.locale || "en-IN";

  const [products, setProducts] = useState([]);
  const [showQSection, setQSection] = useState(false);
  const { id } = useParams();
  const [price, setPrice] = useState(parseFloat(menuItem.price));
  const [showCartButton, setShowCartButton] = useState(true);
  const [inCart, SetinCart] = useState("false");
  const [isDescExpanded, setIsDescExpanded] = useState(false);
  const [showAddOnSheet, setShowAddOnSheet] = useState(false);
  // Map of addOnId -> chosen quantity (0/absent = not selected).
  const [selectedAddOnQuantities, setSelectedAddOnQuantities] = useState({});
  // Multi-variant products sit in the cart as one line per chosen variant
  // (+ add-ons); the card shows their combined quantity.
  const variantLines = isMultiVariant(menuItem)
    ? (cart || []).filter(
        (item) => item.productId === menuItem._id && item.variantId && !item.readonly,
      )
    : [];
  const quantity = isMultiVariant(menuItem)
    ? variantLines.reduce(
        (sum, item) => sum + (itemQuantities[item._id]?.quantity || 0),
        0,
      )
    : itemQuantities[menuItem._id]?.quantity || 0;
  const isAvailable = menuItem.isActive;
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const getLocalizedText = (field) => {
    if (!field) return "";
    if (typeof field === "string") return field;
    if (typeof field === "object")
      return field[i18n.language] || field.en || "";
    return "";
  };
  const { defaultImage } = getAppConfig();
  const fallbackImage = defaultImage
    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
    : "/img/finglider_logo.jpg";
  const imageSrc = menuItem.imageUrl
    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${menuItem.imageUrl}`
    : fallbackImage;
  const handleImageError = (e) => {
    const img = e.currentTarget;
    img.onerror = null;
    img.src = fallbackImage;
  };
  const [showImagePreview, setShowImagePreview] = useState(false);
  const isOffer = menuItem.offerDescription;

  // Close the image preview with Esc
  useEffect(() => {
    if (!showImagePreview) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowImagePreview(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showImagePreview]);
  // const quantity = itemQuantities[menuItem._id]?.quantity || 0;

  // Add this useEffect to sync showCartButton with quantity
  useEffect(() => {
    setShowCartButton(quantity === 0);
  }, [quantity]);

  /*useEffect(() => {
  fetch('/api/products')
      .then(res => res.json())
      .then(data => setProducts([...data])); // Ensures fresh data
}, []);*/

  const hasVariants = menuItem.variants?.length > 0;
  // More than one variant: the customer picks one before adding.
  const isCustomizable = isMultiVariant(menuItem);
  const variant = isCustomizable
    ? cheapestVariant(menuItem)
    : hasVariants
      ? menuItem.variants[0]
      : null;
  const unitLabel = useVariantUnitLabel(lang);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const selectedVariant = isCustomizable
    ? menuItem.variants.find((v) => v._id === selectedVariantId) || variant
    : null;

  const basePrice = hasVariants ? variant?.price : menuItem.price;
  const offerPrice = hasVariants ? variant?.offerPrice : menuItem.offerPrice;
  const unitPrice = Number(offerPrice || basePrice || 0);

  const visibleAddOns = (menuItem.addOns || []).filter(
    (a) => a.isVisible !== false,
  );
  const hasAddOns = visibleAddOns.length > 0;

  // Price on the options sheet's "Add to Cart" button.
  const sheetTotal =
    (isCustomizable ? variantPrice(selectedVariant) : unitPrice) +
    visibleAddOns.reduce(
      (sum, a) => sum + Number(a.price || 0) * (selectedAddOnQuantities[a._id] || 0),
      0,
    );

  // Preserve whatever price (base + any selected add-ons) is already
  // stored for this cart line instead of recomputing from the base
  // price, so bumping quantity doesn't silently drop add-on cost.
  const currentLinePrice = itemQuantities[menuItem._id]?.price ?? unitPrice;

  const handleIncreaseMinQuantity = (minquantity, price) => {
    const newQuantity = minquantity == 0 ? 1 : minquantity;
    updateQuantity(menuItem._id, newQuantity, price);
    //updateTotalPrice(menuItem.price);
    setShowCartButton(false);
    props.handleClick(menuItem);
  };

  const openOptionsSheet = () => {
    setSelectedAddOnQuantities({});
    setSelectedVariantId(variant?._id || null);
    setShowAddOnSheet(true);
  };

  const handleIncreaseQuantity = () => {
    // "+" on a multi-variant product asks which variant to add.
    if (isCustomizable) {
      openOptionsSheet();
      return;
    }
    const newQuantity = quantity + 1;
    updateQuantity(menuItem._id, newQuantity, currentLinePrice);
  };
  const removeItem = (_id) => {
    const removedItem = cart.find((item) => item._id === _id);
    const updatedCart = cart.filter((item) => item._id !== _id);
    props.setCart(updatedCart);
    sessionStorage.setItem("cartValue", JSON.stringify(updatedCart));

    // updateTotalPrice(-removedItem.price);
    setShowCartButton(true);
  };
  const handleDecreaseQuantity = () => {
    if (isCustomizable) {
      // Only unambiguous with a single variant line in the cart.
      if (variantLines.length > 1) {
        enqueueSnackbar(
          t("removeVariantFromCart") ||
            "This item has more than one option in your cart. Remove it from the cart.",
          { variant: "info" },
        );
        return;
      }
      const line = variantLines[0];
      if (!line) return;
      const lineQty = itemQuantities[line._id]?.quantity || 0;
      updateQuantity(line._id, lineQty - 1, itemQuantities[line._id]?.price);
      if (lineQty - 1 <= 0) removeItem(line._id);
      return;
    }
    if (quantity >= 0) {
      const newQuantity = quantity - 1;
      updateQuantity(menuItem._id, newQuantity, currentLinePrice);
      if (newQuantity === 0) {
        removeItem(menuItem._id);
      } else {
        //updateTotalPrice(-menuItem.price);
      }
    }
  };

  const handleAddToCart = () => {
    handleIncreaseQuantity();
    setShowCartButton(false);
  };

  const handleAddButtonClick = () => {
    if (hasAddOns || isCustomizable) {
      openOptionsSheet();
      return;
    }
    props.handleClick(menuItem);
    handleAddToCart();
  };

  const setAddOnQty = (addOnId, qty) => {
    setSelectedAddOnQuantities((prev) => {
      if (qty <= 0) {
        const next = { ...prev };
        delete next[addOnId];
        return next;
      }
      return { ...prev, [addOnId]: qty };
    });
  };

  const confirmAddWithAddOns = () => {
    const chosen = visibleAddOns
      .filter((a) => (selectedAddOnQuantities[a._id] || 0) > 0)
      .map((a) => ({
        addOnId: a._id,
        name: getLocalizedText(a.name),
        price: Number(a.price),
        quantity: selectedAddOnQuantities[a._id],
      }));

    const addOnsTotal = chosen.reduce((sum, a) => sum + a.price * a.quantity, 0);

    if (isCustomizable && selectedVariant) {
      const lineId = variantLineId(menuItem._id, selectedVariant, chosen);
      const linePrice = variantPrice(selectedVariant) + addOnsTotal;
      const existingQty = itemQuantities[lineId]?.quantity || 0;
      const lineInCart = (cart || []).some((item) => item._id === lineId && !item.readonly);
      // Same variant + same add-ons again: just bump that line.
      if (!lineInCart) props.handleClick(menuItem, chosen, selectedVariant);
      updateQuantity(lineId, (lineInCart ? existingQty : 0) + 1, linePrice);
      setShowAddOnSheet(false);
      setShowCartButton(false);
      return;
    }

    props.handleClick(menuItem, chosen);
    updateQuantity(menuItem._id, 1, unitPrice + addOnsTotal);
    setShowAddOnSheet(false);
    setShowCartButton(false);
  };

  /*useEffect(() => {
  if (!id) return; 
  fetch(`/products-by-subcategory?subcatId=${id}`)
    .then(res => res.json())
    .then(data => {
     
      setProducts(data.products);
    })
    .catch(error => console.error("Error fetching products:", error));
}, [id]);*/
  return (
    <div className="w-full min-w-0">
      <div className="flex items-start gap-3 px-3 sm:px-4 py-3.5 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-brand-200 transition-all duration-200 h-full">
        {/* Details Section (left) */}
        <div className="flex-1 min-w-0 pt-0.5">
          <div className="flex items-start gap-1.5 mb-0.5">
            {menuItem.foodType && menuItem.foodType !== "na" && (
              <span
                title={
                  menuItem.foodType === "veg" ? "Vegetarian" : "Non-Vegetarian"
                }
                className="mt-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm flex-shrink-0"
                style={{
                  border: `1.5px solid ${menuItem.foodType === "veg" ? "#22c55e" : "#ef4444"
                    }`,
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{
                    backgroundColor:
                      menuItem.foodType === "veg" ? "#22c55e" : "#ef4444",
                  }}
                />
              </span>
            )}
            <h6 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-tight">
              {getLocalizedText(menuItem.productName)}
            </h6>
          </div>

          {menuItem.isTodaysSpecial && (
            <span className="inline-block mb-1 px-1.5 py-0.5 rounded-full bg-brand-100 text-brand-700 text-[10px] font-bold">
              ⭐ {t("todaysSpecial")}
            </span>
          )}

          {/* Description */}
          {menuItem.description && (
            <div className="text-xs text-gray-500 mb-1.5">
              <p
                className={`${!isDescExpanded ? "line-clamp-2" : ""
                  } leading-snug`}
              >
                {getLocalizedText(menuItem.description)}
              </p>
              {getLocalizedText(menuItem.description).length > 100 && (
                <button
                  onClick={() => setIsDescExpanded(!isDescExpanded)}
                  className="text-brand-600 hover:text-brand-700 font-semibold text-[10px] mt-0.5 transition-colors duration-200"
                >
                  {isDescExpanded ? "Read less" : "Read more"}
                </button>
              )}
            </div>
          )}

          {/* Price Section */}
          {offerPrice ? (
            (() => {
              const discount = Math.round(
                ((basePrice - offerPrice) / basePrice) * 100
              );

              return (
                <div className="flex items-center flex-wrap gap-1.5" dir="ltr">
                  <span className="text-sm font-semibold text-gray-800">
                    {formatCurrency(offerPrice, locale)}
                  </span>
                  <span className="text-[10px] text-gray-400 line-through">
                    {formatCurrency(basePrice, locale)}
                  </span>
                  <span className="bg-green-100 text-green-700 text-[9px] font-medium px-1 py-0.5 rounded">
                    {discount}% OFF
                  </span>
                </div>
              );
            })()
          ) : (
            <span className="text-sm font-semibold text-gray-800">
              {formatCurrency(basePrice, locale)}
            </span>
          )}

          {isCustomizable && (
            <span className="inline-block mt-1 px-1.5 py-0.5 rounded border border-orange-200 bg-orange-50 text-[10px] text-orange-600 font-semibold">
              {t("customizable") || "Customizable"}
            </span>
          )}

          {/* Quantity Sections */}
          {showQSection &&
            menuItem.quantitySections?.map((offeritem, index1) => (
              <button
                key={index1}
                onClick={() => {
                  handleIncreaseMinQuantity(
                    offeritem.minQuantity,
                    offeritem.price,
                  );
                }}
                className="block text-xs text-brand-600 hover:text-brand-700 font-medium mt-1.5"
              >
                ADD {offeritem.name}
              </button>
            ))}
        </div>

        {/* Image Section (right) */}
        <div className="relative flex-shrink-0 w-20 sm:w-24">
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-gray-50">
            {isAvailable === 0 && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center z-10">
                <span className="bg-red-500 text-white px-1.5 py-0.5 rounded text-[9px] font-bold">
                  SOLD OUT
                </span>
              </div>
            )}
            {isOffer && (
              <div className="absolute top-1 left-1 z-10">
                <span className="bg-gradient-to-r from-brand-500 to-brand-700 text-white px-1.5 py-0.5 rounded-full text-[8px] font-bold shadow-md">
                  {isOffer}
                </span>
              </div>
            )}
            {menuItem.isSignatureDish && (
              <div className="absolute top-1 right-1 z-10">
                <span className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-1.5 py-0.5 rounded-full text-[8px] font-bold shadow-md">
                  ★ Signature
                </span>
              </div>
            )}
            <button
              type="button"
              onClick={() => setShowImagePreview(true)}
              className="block w-full h-full cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              aria-label={getLocalizedText(menuItem.productName)}
            >
              <img
                loading="lazy"
                src={imageSrc}
                onError={handleImageError}
                className="w-full h-full object-cover"
                alt={getLocalizedText(menuItem.productName)}
              />
            </button>
          </div>

          {/* Add / quantity control, overlapping the image's bottom-right corner */}
          {showCartButton ? (
            <button
              onClick={handleAddButtonClick}
              className="absolute -bottom-2.5 -right-1.5 flex items-center justify-center w-8 h-8 bg-white border-2 border-brand-500 text-brand-600 rounded-full shadow-md hover:bg-brand-50 active:scale-95 transition-all duration-200"
              aria-label={t("add")}
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>
          ) : (
            <div className="absolute -bottom-2.5 -right-1.5 flex items-center gap-1 bg-white border-2 border-brand-500 rounded-full shadow-md px-1 py-1">
              <button
                onClick={handleDecreaseQuantity}
                className="flex items-center justify-center w-5 h-5 text-brand-600"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M20 12H4"
                  />
                </svg>
              </button>
              <span className="flex items-center justify-center min-w-[14px] text-[11px] font-semibold text-gray-800">
                {formatNumber(quantity, lang)}
              </span>
              <button
                onClick={handleIncreaseQuantity}
                className="flex items-center justify-center w-5 h-5 text-brand-600"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Enlarged image preview */}
      {showImagePreview &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
            onClick={() => setShowImagePreview(false)}
            role="dialog"
            aria-modal="true"
          >
            <button
              type="button"
              onClick={() => setShowImagePreview(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-lg hover:bg-white"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <figure
              className="flex flex-col items-center max-w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={imageSrc}
                onError={handleImageError}
                className="max-w-[92vw] max-h-[80vh] object-contain rounded-2xl shadow-2xl bg-white"
                alt={getLocalizedText(menuItem.productName)}
              />
              <figcaption className="mt-3 text-white text-sm sm:text-base font-semibold text-center">
                {getLocalizedText(menuItem.productName)}
              </figcaption>
            </figure>
          </div>,
          document.body
        )}

      {/* Add-ons selection sheet */}
      {showAddOnSheet && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-end align-items-sm-center justify-content-center"
          style={{ background: "rgba(0,0,0,0.5)", zIndex: 1050 }}
          onClick={() => setShowAddOnSheet(false)}
        >
          <div
            className="bg-white rounded-top-4 rounded-sm-4 w-100 shadow-lg"
            style={{ maxWidth: "420px", maxHeight: "80vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
              <h6 className="mb-0 fw-bold">
                {isCustomizable
                  ? getLocalizedText(menuItem.productName)
                  : `${t("addOnsFor") || "Add-ons for"} ${getLocalizedText(menuItem.productName)}`}
              </h6>
              <button
                type="button"
                className="btn btn-sm btn-light rounded-circle"
                onClick={() => setShowAddOnSheet(false)}
              >
                ✕
              </button>
            </div>

            {isCustomizable && (
              <div className="p-3 border-bottom">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  {t("chooseOption") || "Choose an option"}
                </div>
                {menuItem.variants.map((v) => {
                  const checked = selectedVariant?._id === v._id;
                  const hasOffer = v.offerPrice && Number(v.offerPrice) < Number(v.price);
                  return (
                    <label
                      key={v._id}
                      className={`d-flex align-items-start gap-2 p-2 mb-2 rounded-3 border ${checked ? "border-warning bg-orange-50" : ""}`}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        type="radio"
                        name={`variant-${menuItem._id}`}
                        checked={checked}
                        onChange={() => setSelectedVariantId(v._id)}
                        className="mt-1"
                        style={{ accentColor: "#f97316" }}
                      />
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="text-sm fw-semibold">{unitLabel(v.unit)}</div>
                        {v.description && (
                          <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                            {v.description}
                          </div>
                        )}
                      </div>
                      <div className="text-end" dir="ltr">
                        <div className="text-sm fw-semibold">
                          {formatCurrency(variantPrice(v), locale)}
                        </div>
                        {hasOffer && (
                          <div className="text-muted text-decoration-line-through" style={{ fontSize: "0.7rem" }}>
                            {formatCurrency(v.price, locale)}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            {hasAddOns && (
            <div className="p-3">
              {isCustomizable && (
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  {t("addOns") || "Add-ons"}
                </div>
              )}
              {visibleAddOns.map((addOn) => {
                const qty = selectedAddOnQuantities[addOn._id] || 0;
                return (
                  <div
                    key={addOn._id}
                    className="d-flex align-items-center justify-content-between py-2 border-bottom"
                  >
                    <div className="d-flex align-items-center gap-2">
                      {addOn.imageUrl && (
                        <img
                          loading="lazy"
                          src={`${process.env.REACT_APP_IMAGE_BASE_URL}${addOn.imageUrl}`}
                          onError={(e) => {
                            const img = e.currentTarget;
                            img.onerror = null;
                            img.src = defaultImage
                              ? `${process.env.REACT_APP_IMAGE_BASE_URL}${defaultImage}`
                              : "/img/finglider_logo.jpg";
                          }}
                          alt={getLocalizedText(addOn.name)}
                          className="rounded"
                          style={{ width: "36px", height: "36px", objectFit: "cover", flexShrink: 0 }}
                        />
                      )}
                      <div>
                        <div className="text-sm">{getLocalizedText(addOn.name)}</div>
                        <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                          +{formatCurrency(addOn.price, locale)} each
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setAddOnQty(addOn._id, qty - 1)}
                        disabled={qty === 0}
                        className="btn btn-sm btn-light border rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "28px", height: "28px", padding: 0 }}
                      >
                        −
                      </button>
                      <span
                        className="text-sm fw-semibold text-center"
                        style={{ minWidth: "16px" }}
                      >
                        {qty}
                      </span>
                      <button
                        type="button"
                        onClick={() => setAddOnQty(addOn._id, qty + 1)}
                        className="btn btn-sm btn-light border rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: "28px", height: "28px", padding: 0 }}
                      >
                        +
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            <div className="p-3 border-top">
              <button
                type="button"
                onClick={confirmAddWithAddOns}
                className="w-100 py-2 text-sm font-medium bg-gradient-to-r from-brand-500 to-brand-600 text-white border-0 rounded-xl"
                style={{ background: "linear-gradient(to right, rgb(var(--brand-500)), rgb(var(--brand-600)))" }}
              >
                {t("addToCart") || "Add to Cart"}
                {" · "}
                <span dir="ltr">{formatCurrency(sheetTotal, locale)}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MenuItem;
